import type { Queryfilter as QueryFilter } from '@gonfalon/queryfilter/builder';
import { and, anyOf, equals, toString as queryfilterToString } from '@gonfalon/queryfilter/builder';

import { ApiVersions } from '../api-versions';
import type { GetMetricGroupsOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export const apiVersion = ApiVersions.Beta;

export type GetMetricGroupsQueryParams = Omit<GetMetricGroupsOpenAPIQueryParams, 'filter' | 'expand'> & {
  expand?: Array<
    'experiments' | 'experimentCount' | 'guardedRolloutCount' | 'includeActiveCounts' | 'releasePolicyCount'
  >;
  filter?: {
    experimentStatus?: string;
    query?: string;
    kind?: string;
    hasConnections?: boolean;
    maintainerIds?: string | string[];
    maintainerTeamKey?: string;
    usedIn?: 'experiments' | 'guarded_rollouts' | 'release_policies' | 'any' | 'none';
  };
};

export function querySerializer(queryParams: GetMetricGroupsQueryParams) {
  const searchParams = new URLSearchParams();

  if ((queryParams.limit ?? 0) > 0) {
    searchParams.set('limit', String(queryParams.limit));
  }

  if ((queryParams.offset ?? 0) > 0) {
    searchParams.set('offset', String(queryParams.offset));
  }

  if (queryParams.sort) {
    searchParams.set('sort', String(queryParams.sort));
  }

  if (queryParams.expand && queryParams.expand.length > 0) {
    searchParams.set('expand', queryParams.expand.join(','));
  }

  if (queryParams.filter) {
    const serializedFilter = serializeMetricGroupFilterParam(queryParams.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  return searchParams.toString();
}

export function serializeMetricGroupFilterParam(value: NonNullable<GetMetricGroupsQueryParams['filter']>) {
  const filter: QueryFilter[] = [];

  if (value.experimentStatus) {
    filter.push(equals('experimentStatus', value.experimentStatus));
  }

  if (value.query) {
    filter.push(equals('query', value.query));
  }

  if (value.kind) {
    filter.push(equals('kind', value.kind));
  }

  if (value.maintainerIds) {
    // Convert to array if it's a string
    const idsArray = Array.isArray(value.maintainerIds) ? value.maintainerIds : [value.maintainerIds];

    // This should match the format that works
    filter.push(anyOf('maintainerIds', idsArray));
  }

  if (value.hasConnections !== undefined) {
    filter.push(equals('hasConnections', value.hasConnections));
  }

  if (value.maintainerTeamKey) {
    filter.push(equals('maintainerTeamKey', value.maintainerTeamKey));
  }

  if (value.usedIn) {
    filter.push(equals('metricGroupUsedIn', value.usedIn));
  }

  return queryfilterToString(and(filter));
}
